/* eslint-disable import/extensions */
import { AssignmentFormData, assignmentFormDefaultValues } from '../../types/assignmentTypes';
import {
  Box,
  Button,
  ButtonGroup,
  Center,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  Spinner,
  Stack,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  MatchingCriteria,
  WorkflowResultGroup,
  WorkflowType,
} from '@/client/services/api/graphql/gql/graphql';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Calendar } from '@/client/components/icons/ContinuIcons';
import { ChevronDownIcon } from '@chakra-ui/icons';
import Lottie from 'lottie-react';
import { format } from 'date-fns';
import startNewInstanceAnimation from '@/client/assets/lottie/start-new-instance.json';
import { useFormContext } from 'react-hook-form';
import { useListWorkflowResultGroups } from '../../hooks/useListWorkflowResultGroups';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

interface CreateNewInstanceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CreateNewInstanceModal({ isOpen, onClose }: CreateNewInstanceModalProps) {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { reset } = useFormContext<AssignmentFormData>();
  const [usePreFill, setUsePreFill] = useState(true);
  const [selectedResultGroup, setSelectedResultGroup] = useState<WorkflowResultGroup | null>(null);

  const { data, isLoading, isError } = useListWorkflowResultGroups(
    !!location.state && location.state.workflowId && usePreFill,
    { pageIndex: 0, pageSize: 10000 },
    location?.state?.workflowId,
  );

  useEffect(() => {
    if (data && data.edges && data.edges.length > 0) {
      setSelectedResultGroup(data.edges[0].node);
    }
  }, [data]);

  useEffect(() => {
    if (!usePreFill) {
      reset({
        ...assignmentFormDefaultValues,
        bulkAudienceImportData: {
          usesBulkAudienceImport: false,
          bulkImportFileName: '',
        },
        simpleType: 'pointInTime',
        settings: {
          ...assignmentFormDefaultValues.settings,
          name: selectedResultGroup?.settings.name,
          type: selectedResultGroup?.settings.type,
        },
      });

      setSelectedResultGroup(null);

      return;
    }

    if (data && data?.edges.length > 0) {
      setSelectedResultGroup(data?.edges[0].node);
    }
  }, [usePreFill]);

  useEffect(() => {
    if (selectedResultGroup) {
      const { audience } = selectedResultGroup;

      const matchAnySelections = audience.filter(
        (item) => item.matchingCriteria === MatchingCriteria.MatchAny,
      );
      const matchAllSelections = audience.filter(
        (item) => item.matchingCriteria === MatchingCriteria.MatchAll,
      );
      const matchNoneSelections = audience.filter(
        (item) => item.matchingCriteria === MatchingCriteria.MatchNone,
      );

      reset({
        audience: selectedResultGroup.audience,
        id: location.state?.workflowId,
        simpleType: 'pointInTime',
        formBoundType: WorkflowType.PointInTime,
        addEscalationContacts: selectedResultGroup.settings.escalationContacts.length > 0,
        setDueDate:
          (selectedResultGroup.settings.daysToComplete &&
            selectedResultGroup.settings.daysToComplete > 0) ||
          false,
        useCustomDueDate: false,
        bulkAudienceImportData: {
          usesBulkAudienceImport: false,
          bulkImportFileName: '',
        },
        settings: {
          ...selectedResultGroup.settings,
          name: selectedResultGroup.settings.name,
          type: selectedResultGroup.settings.type,
        },
      });
    }
  }, [selectedResultGroup]);

  if (isError) {
    setToast({
      show: true,
      status: 'error',
      title: t('admin.assignments.instances.createModal.error'),
    });
  }

  return (
    <Modal size="lg" closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />

      <ModalContent borderRadius="16px" paddingTop={10}>
        <ModalBody marginBottom={10}>
          <Box
            height={272}
            width="full"
            backgroundColor="brand.primary"
            backgroundImage="url(/public/images/new-assignment.jpg)"
            animation="fadeIn 1s ease-in"
            sx={{
              '@keyframes fadeIn': {
                '0%': { opacity: 0 },
                '100%': { opacity: 1 },
              },
            }}
            backgroundSize="cover"
          />

          <VStack textAlign="center" marginY={10}>
            <Text fontSize="24px" fontWeight={600}>
              {t('admin.assignments.instances.createModal.label')}
            </Text>

            <Text fontSize="16px" fontWeight="600" color="neutral.600">
              {t('admin.assignments.instances.createModal.helpText')}
            </Text>
          </VStack>

          <Flex alignItems="center" justifyContent="space-between">
            <Stack spacing={0.5}>
              <Text variant="createLabel">
                {t('admin.assignments.instances.createModal.preFill.label')}
              </Text>

              <Text variant="createHelpText">
                {t('admin.assignments.instances.createModal.preFill.helpText')}
              </Text>
            </Stack>

            <Switch
              variant="create"
              isChecked={usePreFill}
              onChange={() => setUsePreFill(!usePreFill)}
            />
          </Flex>

          {usePreFill &&
            (isLoading ? (
              <Center marginTop={6}>
                <Spinner />
              </Center>
            ) : (
              <Menu>
                <MenuButton
                  as={Button}
                  marginTop={6}
                  width="full"
                  backgroundColor="white"
                  color="baseBlack"
                  textAlign="left"
                  fontSize="14px"
                  fontWeight={500}
                  paddingY="14px"
                  paddingX={3}
                  border="1px solid"
                  borderColor="warmNeutral.200"
                  borderRadius="6px"
                  _hover={{ backgroundColor: 'warmNeutral.0' }}
                  _active={{ backgroundColor: 'warmNeutral.0' }}
                  rightIcon={<ChevronDownIcon />}
                  leftIcon={<Calendar />}
                >
                  {selectedResultGroup
                    ? format(new Date(selectedResultGroup.createdAt), 'MMMM d, yyyy h:mm a')
                    : t('admin.assignments.instances.createModal.preFill.selectPreviousInstance')}
                </MenuButton>

                <MenuList
                  border="1px solid"
                  borderColor="warmNeutral.200"
                  borderRadius="6px"
                  boxShadow="0px 4px 12px 0px rgba(0, 0, 0, 0.15)"
                  paddingX={4}
                  maxHeight="300px"
                  overflowY="auto"
                >
                  {data?.edges.map((edge, index) => (
                    <MenuItem
                      key={edge.node.id}
                      color="baseBlack"
                      fontSize="14px"
                      fontWeight={500}
                      borderBottom={index !== data.edges.length - 1 ? '1px solid' : 'none'}
                      borderBottomColor="neutral.100"
                      paddingY={4}
                      _hover={{ backgroundColor: 'warmNeutral.0' }}
                      onClick={() => setSelectedResultGroup(edge.node)}
                    >
                      {format(new Date(edge.node.createdAt), 'MMMM d, yyyy h:mm a')}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
            ))}
        </ModalBody>

        <ModalFooter
          display="flex"
          justifyContent="center"
          backgroundColor="neutral.0"
          borderBottomRadius="16px"
          paddingBottom={12}
        >
          <ButtonGroup gap={4}>
            <Button variant="createCancel" onClick={() => navigate(-1)}>
              {t('global.actions.cancel')}
            </Button>

            <Button isDisabled={usePreFill && isLoading} variant="solid" onClick={onClose}>
              {t('admin.assignments.instances.createModal.preFill.submit')}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
